import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileProductsOpen, setIsMobileProductsOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const location = useLocation();

  // Fetch products for the menu
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await fetch('https://api.dev.auxo.dk/public/content/product');
        if (res.ok) {
          const data = await res.json();
          setProducts(data);
        }
      } catch (err) {
        console.error('Error fetching products for header menu:', err);
      }
    };
    fetchProducts();
  }, []);

  // Close menus on route change
  useEffect(() => {
    setIsOpen(false);
    setIsDropdownOpen(false);
    setIsMobileProductsOpen(false);
  }, [location]);

  // Prevent scroll when mobile menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Helper to get series name
  const getSeriesName = (series) => {
    if (!series) return 'Andre';
    return series.trim();
  };

  // Group products by series
  const groupedProducts = products.reduce((acc, prod) => {
    const series = getSeriesName(prod.product_series);
    if (!acc[series]) {
      acc[series] = [];
    }
    acc[series].push(prod);
    return acc;
  }, {});

  // Sort series names: STAR T first, STAR H second, others alphabetically
  const sortedSeries = Object.keys(groupedProducts).sort((a, b) => {
    const order = { 'STAR T': 1, 'STAR H': 2, 'STAR H Series': 2, 'STAR Q Series': 3 };
    const normA = a.replace(' Series', '');
    const normB = b.replace(' Series', '');
    const orderA = order[a] || order[normA] || 999;
    const orderB = order[b] || order[normB] || 999;
    if (orderA !== orderB) {
      return orderA - orderB;
    }
    return a.localeCompare(b);
  });

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md">
      <div className="container mx-auto px-4 py-4 md:py-6 lg:py-0 lg:h-20 flex flex-col md:items-start lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center justify-between w-full lg:w-auto">
          <Link to="/" className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
            <img src="/Screenshot_2026-04-29_at_15.35.22-removebg-preview.png" alt="GMA Logo" className="h-10 md:h-16 lg:h-10 w-auto object-contain drop-shadow-xl" />
            <div className="w-px h-8 md:h-10 lg:h-6 bg-zinc-200 hidden sm:block"></div>
            <img src="https://assets.auxodev.dk/assets/f4225015-9d61-46d9-9e4c-356d5fd3c807/2e4fbde1-e418-4ccb-b10f-30b3d1b42720.png" alt="Partner Logo" className="h-8 md:h-14 lg:h-8 w-auto object-contain" />
          </Link>

          {/* Mobile Menu Toggle - only visible on screens smaller than md */}
          <button 
            className="md:hidden text-slate-900 p-2"
            onClick={() => setIsOpen(!isOpen)}
            aria-label="Toggle menu"
          >
            {isOpen ? <X size={32} /> : <Menu size={32} />}
          </button>
        </div>
        
        {/* Navigation - Stacked under logo on tablet, row on desktop */}
        <nav className="hidden md:flex flex-col md:flex-row items-start md:items-center gap-6 md:gap-8 lg:gap-10 mt-6 lg:mt-0">
          <Link 
            to="/" 
            className={`text-sm font-semibold text-zinc-800 hover:text-black uppercase tracking-widest transition-colors ${
              location.pathname === '/' ? 'text-black font-black' : ''
            }`}
          >
            Forside
          </Link>

          {/* Dynamic Produkter Dropdown */}
          <div 
            className="relative py-4"
            onMouseEnter={() => setIsDropdownOpen(true)}
            onMouseLeave={() => setIsDropdownOpen(false)}
          >
            <Link 
              to="/produkter" 
              className={`text-sm font-semibold text-zinc-800 hover:text-black uppercase tracking-widest transition-colors flex items-center gap-1 ${
                location.pathname === '/produkter' ? 'text-black font-black' : ''
              }`}
            >
              Produkter
              <svg className={`w-4 h-4 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </Link>

            {/* Dropdown Panel */}
            <AnimatePresence>
              {isDropdownOpen && products.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  transition={{ duration: 0.2 }}
                  className="absolute left-1/2 -translate-x-1/2 top-full w-[640px] bg-white shadow-2xl border border-zinc-100 p-6 grid grid-cols-3 gap-6 z-50"
                >
                  {sortedSeries.map((seriesName) => {
                    const seriesProducts = groupedProducts[seriesName];
                    return (
                      <div key={seriesName} className="space-y-3">
                        <div className="text-[11px] font-black text-[#62b2c9] uppercase tracking-widest border-b border-zinc-100 pb-1.5">
                          {seriesName}
                        </div>
                        <ul className="space-y-2">
                          {seriesProducts.map((prod) => {
                            const titleText = prod.title?.da || prod.title || 'Uden navn';
                            const slug = prod.slug?.da || prod.slug || prod.id;
                            return (
                              <li key={prod.id}>
                                <Link
                                  to={`/produkter?p=${slug}`}
                                  onClick={() => setIsDropdownOpen(false)}
                                  className="text-xs font-bold text-zinc-600 hover:text-black uppercase tracking-wider transition-colors block py-0.5"
                                >
                                  {titleText}
                                </Link>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    );
                  })}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <Link 
            to="/projekter" 
            className={`text-sm font-semibold text-zinc-800 hover:text-black uppercase tracking-widest transition-colors ${
              location.pathname === '/projekter' ? 'text-black font-black' : ''
            }`}
          >
            Projekter
          </Link>

          <Link 
            to="/om-os" 
            className={`text-sm font-semibold text-zinc-800 hover:text-black uppercase tracking-widest transition-colors ${
              location.pathname === '/om-os' ? 'text-black font-black' : ''
            }`}
          >
            Om os
          </Link>

          <Link to="/kontakt" className="bg-slate-900 hover:bg-black text-white px-6 py-2.5 rounded-none text-sm font-bold uppercase tracking-wider transition-all shadow-sm">
            Kontakt os
          </Link>
        </nav>
      </div>

      {/* Mobile Sidebar - only for screens smaller than md */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[51] md:hidden"
            />
            
            {/* Sidebar Container */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed top-0 right-0 h-full w-[80%] max-w-[400px] bg-white z-[52] md:hidden shadow-2xl flex flex-col"
            >
              <div className="p-6 flex justify-end">
                <button 
                  onClick={() => setIsOpen(false)}
                  className="text-slate-900 p-2"
                  aria-label="Close menu"
                >
                  <X size={32} />
                </button>
              </div>

              <nav className="flex flex-col gap-6 px-10 pt-10 overflow-y-auto max-h-[calc(100vh-220px)]">
                <Link 
                  to="/" 
                  onClick={() => setIsOpen(false)}
                  className={`text-2xl font-bold text-zinc-800 hover:text-black uppercase tracking-widest transition-colors ${
                    location.pathname === '/' ? 'text-black' : ''
                  }`}
                >
                  Forside
                </Link>

                {/* Mobile Produkter Accordion */}
                <div className="flex flex-col">
                  <button 
                    onClick={() => setIsMobileProductsOpen(!isMobileProductsOpen)}
                    className={`text-2xl font-bold text-zinc-800 hover:text-black uppercase tracking-widest transition-colors flex items-center justify-between w-full text-left ${
                      location.pathname === '/produkter' ? 'text-black' : ''
                    }`}
                  >
                    <span>Produkter</span>
                    <svg className={`w-6 h-6 transition-transform duration-200 ${isMobileProductsOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  <AnimatePresence>
                    {isMobileProductsOpen && products.length > 0 && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        className="overflow-hidden pl-4 mt-4 flex flex-col gap-4 border-l border-zinc-100"
                      >
                        {sortedSeries.map((seriesName) => {
                          const seriesProducts = groupedProducts[seriesName];
                          return (
                            <div key={seriesName} className="space-y-2">
                              <div className="text-[10px] font-black text-[#62b2c9] uppercase tracking-widest">
                                {seriesName}
                              </div>
                              <div className="flex flex-col gap-2 pl-2">
                                {seriesProducts.map((prod) => {
                                  const titleText = prod.title?.da || prod.title || 'Uden navn';
                                  const slug = prod.slug?.da || prod.slug || prod.id;
                                  return (
                                    <Link
                                      key={prod.id}
                                      to={`/produkter?p=${slug}`}
                                      onClick={() => {
                                        setIsOpen(false);
                                        setIsMobileProductsOpen(false);
                                      }}
                                      className="text-base font-semibold text-zinc-600 hover:text-black uppercase tracking-wider transition-colors"
                                    >
                                      {titleText}
                                    </Link>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                <Link 
                  to="/projekter" 
                  onClick={() => setIsOpen(false)}
                  className={`text-2xl font-bold text-zinc-800 hover:text-black uppercase tracking-widest transition-colors ${
                    location.pathname === '/projekter' ? 'text-black' : ''
                  }`}
                >
                  Projekter
                </Link>

                <Link 
                  to="/om-os" 
                  onClick={() => setIsOpen(false)}
                  className={`text-2xl font-bold text-zinc-800 hover:text-black uppercase tracking-widest transition-colors ${
                    location.pathname === '/om-os' ? 'text-black' : ''
                  }`}
                >
                  Om os
                </Link>
                
                <div className="mt-8 pt-8 border-t border-zinc-100">
                  <Link 
                    to="/kontakt" 
                    onClick={() => setIsOpen(false)} 
                    className="inline-block bg-slate-900 text-white px-8 py-4 rounded-none text-lg font-black uppercase tracking-widest transition-all text-center w-full shadow-lg"
                  >
                    Kontakt os
                  </Link>
                </div>
              </nav>

              <div className="mt-auto p-10 flex flex-col gap-6 border-t border-zinc-50">
                <img src="/Screenshot_2026-04-29_at_15.35.22-removebg-preview.png" alt="GMA Logo" className="h-12 w-auto object-contain self-start drop-shadow-lg" />
                <img src="https://assets.auxodev.dk/assets/f4225015-9d61-46d9-9e4c-356d5fd3c807/2e4fbde1-e418-4ccb-b10f-30b3d1b42720.png" alt="Partner Logo" className="h-10 w-auto object-contain self-start" />
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
